// Importação em massa e exportação (PDF/imagem) da Tabela de Preços (issue #25).

import {
  precoBaseVigente,
  precoVigente,
} from "../../domain/precificacao.ts";
import { formatReais } from "../../domain/money.ts";
import { formatData, hojeISO, isDataISO } from "../../domain/datas.ts";
import type { Snapshot, TipoPeca } from "../../domain/snapshot.ts";
import type { Store } from "../store.ts";
import { clear, el, openModal, botao } from "../ui.ts";
import { criarAcaoComando } from "./_acao.ts";
import { inputData } from "./_cadastro.ts";
import {
  ALTURA_RODAPE_MARCA,
  alturaCabecalhoFolha,
  baixarCanvasJpeg,
  baixarTexto,
  type CabecalhoFolha,
  carregarLogo,
  cor,
  cortar,
  desenharCabecalhoFolha,
  desenharRodapeMarca,
  fonteDoc,
  imprimirCanvas,
  nomeArquivo,
} from "./_folha-canvas.ts";
import {
  analisarImport,
  contarAplicaveis,
  itensDaAnalise,
  matrizCsv,
  type AnaliseImport,
  type Alvo,
} from "./_precos-parse.ts";

function tiposAtivosOrdenados(snap: Snapshot): TipoPeca[] {
  return [...snap.tiposPeca]
    .filter((t) => t.ativo)
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

// ---------------------------------------------------------------- exportar ----

interface ItemExport {
  nome: string;
  preco: string | null; // null = sem preço vigente
}
interface GrupoExport {
  categoria: string;
  itens: ItemExport[];
}
interface OpcoesExport {
  titulo: string;
  /** nome do hotel (vai antes de "Data:" na faixa azul), ou "" no preço base. */
  subtitulo: string;
  grupos: GrupoExport[];
  semPreco: "consulta" | "ocultar";
  /** Cliente da tabela, para o nome do arquivo; `null` no preço base. */
  cliente: string | null;
}

export function abrirExportarPrecos(store: Store): void {
  const snap = store.get();

  openModal("Exportar Tabela de Preços", (fechar) => {
    // sempre abre em "Preço base"
    const selAlvo = el("select", {}) as HTMLSelectElement;
    selAlvo.append(
      el("option", { value: "base" }, "Preço Base (todos os tipos)"),
    );
    for (const c of [...snap.clientes]
      .filter((c) => c.ativo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))) {
      selAlvo.append(
        el("option", { value: c.id }, `Preços de ${c.nome}`) as HTMLOptionElement,
      );
    }
    selAlvo.append(
      el("option", { value: "matriz" }, "Matriz — base + todos os hotéis (só .csv)"),
    );

    // árvore categoria → peça
    const cats = [...snap.categorias]
      .filter((c) => c.ativo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    const porCat = new Map<string, TipoPeca[]>();
    for (const t of tiposAtivosOrdenados(snap)) {
      const arr = porCat.get(t.categoriaId);
      if (arr) arr.push(t);
      else porCat.set(t.categoriaId, [t]);
    }
    const checkTipo = new Map<string, HTMLInputElement>();
    const checkCat = new Map<string, HTMLInputElement>();
    const arvore = el("div", { class: "export-arvore" });

    const sincCat = (catId: string): void => {
      const tipos = porCat.get(catId) ?? [];
      const marcados = tipos.filter((t) => checkTipo.get(t.id)!.checked).length;
      const cc = checkCat.get(catId)!;
      cc.checked = tipos.length > 0 && marcados === tipos.length;
      cc.indeterminate = marcados > 0 && marcados < tipos.length;
    };

    for (const cat of cats) {
      const tipos = porCat.get(cat.id) ?? [];
      if (tipos.length === 0) continue;
      const cc = el("input", { type: "checkbox" }) as HTMLInputElement;
      cc.checked = true;
      checkCat.set(cat.id, cc);
      cc.addEventListener("change", () => {
        for (const t of tipos) checkTipo.get(t.id)!.checked = cc.checked;
      });
      const bloco = el(
        "div",
        { class: "export-cat" },
        el("label", { class: "export-cat-h" }, cc, ` ${cat.nome}`),
      );
      for (const t of tipos) {
        const tc = el("input", { type: "checkbox" }) as HTMLInputElement;
        tc.checked = true;
        checkTipo.set(t.id, tc);
        tc.addEventListener("change", () => sincCat(cat.id));
        bloco.append(el("label", { class: "export-peca" }, tc, ` ${t.nome}`));
      }
      arvore.append(bloco);
    }

    const marcarTipos = (querSelecionado: (t: TipoPeca) => boolean): void => {
      for (const [id, tc] of checkTipo) {
        const t = snap.tiposPeca.find((x) => x.id === id);
        tc.checked = !!t && querSelecionado(t);
      }
      for (const cat of cats) sincCat(cat.id);
    };
    const btnMarcar = botao("link", {}, "Marcar Tudo");
    btnMarcar.addEventListener("click", () => marcarTipos(() => true));
    const btnDesmarcar = botao("link", {}, "Desmarcar Tudo");
    btnDesmarcar.addEventListener("click", () => marcarTipos(() => false));
    const btnComPreco = botao(
      "link",
      {},
      "Só os que Têm Preço",
    );
    btnComPreco.addEventListener("click", () => {
      const alvo = selAlvo.value;
      const hoje = hojeISO();
      marcarTipos((t) =>
        alvo === "base"
          ? precoBaseVigente(snap, t.id, hoje) != null
          : !precoVigente(snap, alvo, t.id, hoje).pendente,
      );
    });
    const selAcoes = el(
      "div",
      { class: "export-sel-acoes" },
      btnMarcar,
      btnDesmarcar,
      btnComPreco,
    );

    const semPrecoOcultar = el("input", {
      type: "radio",
      name: "exp-sem-preco",
      value: "ocultar",
    }) as HTMLInputElement;
    const semPrecoConsulta = el("input", {
      type: "radio",
      name: "exp-sem-preco",
      value: "consulta",
    }) as HTMLInputElement;
    semPrecoConsulta.checked = true;

    const colher = (): OpcoesExport => {
      const alvo = selAlvo.value;
      const hoje = hojeISO();
      const catNome = new Map(snap.categorias.map((c) => [c.id, c.nome]));
      const grupos: GrupoExport[] = [];
      for (const cat of cats) {
        const tipos = (porCat.get(cat.id) ?? []).filter(
          (t) => checkTipo.get(t.id)!.checked,
        );
        if (tipos.length === 0) continue;
        const itens: ItemExport[] = tipos.map((t) => {
          if (alvo === "base") {
            const v = precoBaseVigente(snap, t.id, hoje);
            return { nome: t.nome, preco: v == null ? null : formatReais(v) };
          }
          const r = precoVigente(snap, alvo, t.id, hoje);
          return {
            nome: t.nome,
            preco: r.pendente ? null : formatReais(r.valorCentavos),
          };
        });
        grupos.push({ categoria: catNome.get(cat.id) ?? cat.nome, itens });
      }
      const cli =
        alvo === "base" ? null : snap.clientes.find((c) => c.id === alvo);
      return {
        titulo: cli ? `Tabela de Preços — ${cli.nome}` : "Tabela de Preços",
        subtitulo: cli ? cli.nome : "",
        grupos,
        semPreco: semPrecoOcultar.checked ? "ocultar" : "consulta",
        cliente: cli ? cli.nome : null,
      };
    };

    const rodape = snap.config.rodapeExport;

    // padrão do sistema: um seletor de formato + um único botão de ação
    const selFormato = el("select", {}) as HTMLSelectElement;
    const optsFormato: Record<string, HTMLOptionElement> = {};
    for (const [v, r] of [
      ["pdf", "PDF"],
      ["jpeg", "Imagem (JPEG)"],
      ["csv", "Planilha (CSV)"],
    ] as const) {
      const o = el("option", { value: v }, r) as HTMLOptionElement;
      optsFormato[v] = o;
      selFormato.append(o);
    }

    const exportar = botao(
      "primario",
      { type: "submit" },
      "Exportar",
    );
    const cancelar = botao("secundario", {}, "Cancelar");
    cancelar.addEventListener("click", fechar);

    const detalhe = el(
      "div",
      { class: "export-detalhe" },
      el("span", { class: "fld-sub" }, "Categorias e peças"),
      selAcoes,
      arvore,
      el(
        "fieldset",
        { class: "export-sem-preco" },
        el("legend", {}, "Peças sem Preço"),
        el("label", { class: "check inline" }, semPrecoConsulta, " Mostrar como “Sob Consulta”"),
        el("label", { class: "check inline" }, semPrecoOcultar, " Não incluir"),
      ),
    );

    // a matriz só sai em CSV — trava o formato e esconde a seleção fina
    const sincFormato = (): void => {
      const matriz = selAlvo.value === "matriz";
      optsFormato["pdf"]!.disabled = matriz;
      optsFormato["jpeg"]!.disabled = matriz;
      if (matriz) selFormato.value = "csv";
      selFormato.disabled = matriz;
      detalhe.hidden = matriz;
    };
    selAlvo.addEventListener("change", sincFormato);
    sincFormato();

    const form = el(
      "form",
      { class: "form-modal" },
      el("label", {}, "O que Exportar", selAlvo),
      detalhe,
      el("label", {}, "Formato", selFormato),
      el("div", { class: "modal-actions" }, cancelar, exportar),
    );
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      if (selAlvo.value === "matriz") {
        fechar();
        baixarMatrizCsv(snap);
        return;
      }
      const o = colher();
      const fmt = selFormato.value;
      fechar();
      if (fmt === "csv") {
        baixarCsvExport(o);
      } else if (fmt === "jpeg") {
        void gerarCanvas(o, rodape).then((cv) =>
          baixarCanvasJpeg(cv, nomeArquivo("tabela-precos", o.cliente, hojeISO(), "jpg")),
        );
      } else {
        void gerarCanvas(o, rodape).then((cv) =>
          imprimirCanvas(cv, nomeArquivo("tabela-precos", o.cliente, hojeISO())),
        );
      }
    });
    return form;
  }, "largo");
}

/** Baixa a tabela selecionada como CSV (categoria;tipo de peça;preço). */
function baixarCsvExport(o: OpcoesExport): void {
  const linhas = ["categoria;tipo de peça;preço"];
  for (const g of o.grupos) {
    for (const it of g.itens) {
      if (it.preco == null && o.semPreco === "ocultar") continue;
      const preco =
        it.preco == null ? "Sob Consulta" : it.preco.replace(/R\$\s?/i, "");
      linhas.push(`${g.categoria};${it.nome};${preco}`);
    }
  }
  baixarTexto(nomeArquivo("tabela-precos", o.cliente, hojeISO(), "csv"), linhas.join("\r\n"));
}


function espacar(s: string): string {
  return s.split("").join(" ");
}

async function gerarCanvas(
  o: OpcoesExport,
  rodape: { slogan: string; whatsapp: string },
): Promise<HTMLCanvasElement> {
  const logo = await carregarLogo();

  const W = 520;
  const S = 3;
  const PAD = 36;
  const hCabecalho = alturaCabecalhoFolha(false);
  const hRodape = ALTURA_RODAPE_MARCA;
  const rowH = 30;

  const grupos = o.grupos
    .map((g) => ({
      categoria: g.categoria,
      itens: g.itens.filter((i) => i.preco != null || o.semPreco === "consulta"),
    }))
    .filter((g) => g.itens.length > 0);

  let hCorpo = 16;
  for (const g of grupos) hCorpo += 38 + g.itens.length * rowH + 12;
  const H = hCabecalho + hCorpo + hRodape;

  const cab: CabecalhoFolha = {
    titulo: "TABELA DE PREÇOS",
    subtitulo: `${o.subtitulo ? `${o.subtitulo} · ` : ""}Data: ${formatData(hojeISO())}`,
  };

  const cv = document.createElement("canvas");
  cv.width = W * S;
  cv.height = H * S;
  const ctx = cv.getContext("2d");
  if (!ctx) return cv;
  ctx.scale(S, S);
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = cor("surface");
  ctx.fillRect(0, 0, W, H);

  // faixa azul (logo pequena + título em branco) — mesma marca da Planilha
  desenharCabecalhoFolha(ctx, W, logo, cab);

  // grupos
  let y = hCabecalho + 6;
  for (const g of grupos) {
    y += 28;
    ctx.fillStyle = cor("muted");
    ctx.font = fonteDoc("lista", "700");
    ctx.textAlign = "left";
    ctx.fillText(espacar(g.categoria.toUpperCase()), PAD, y);
    y += 8;
    for (const it of g.itens) {
      y += rowH;
      ctx.strokeStyle = cor("surface-alt");
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PAD, y);
      ctx.lineTo(W - PAD, y);
      ctx.stroke();
      ctx.textAlign = "left";
      ctx.fillStyle = cor("ink");
      ctx.font = fonteDoc("lista");
      ctx.fillText(cortar(ctx, it.nome, W - PAD * 2 - 120), PAD, y - 10);
      ctx.textAlign = "right";
      if (it.preco == null) {
        ctx.fillStyle = cor("muted");
        ctx.font = fonteDoc("lista", "italic");
        ctx.fillText("Sob Consulta", W - PAD, y - 10);
      } else {
        ctx.fillStyle = cor("brand-navy");
        ctx.font = fonteDoc("lista", "700");
        ctx.fillText(it.preco, W - PAD, y - 10);
      }
    }
    y += 10;
  }

  desenharRodapeMarca(ctx, W, H - hRodape, rodape);
  return cv;
}


// ---------------------------------------------------------------- importar ----

export function abrirImportarPrecos(
  store: Store,
  clienteIdAtual: string | null,
  aoAplicar: () => void,
): void {
  const snap = store.get();
  const alvoImplicito: Alvo = clienteIdAtual ?? "base";

  openModal("Importar Preços em Massa", (fechar) => {
    const baixarModelo = botao(
      "link",
      {},
      "Baixar Modelo com Todos os Hotéis (.csv)",
    );
    baixarModelo.addEventListener("click", () => baixarMatrizCsv(snap));

    const dataInp = inputData(hojeISO());

    const arquivo = el("input", {
      type: "file",
      accept: ".csv,.txt,.tsv",
    }) as HTMLInputElement;
    const cola = el("textarea", {
      rows: "6",
      placeholder:
        "Cole a planilha. 1ª linha = cabeçalho com os nomes das colunas.\n" +
        "Ex.:\n  tipo de peça;Preço base;Hotel Central Plaza;Pousada Vista Mar\n" +
        "  Toalha de banho;2,50;2,20;\n  Lençol casal;;;3,10",
    }) as HTMLTextAreaElement;

    const previa = el("div", { class: "import-previa" });
    const erroEl = el("p", { class: "form-erro" });
    let analise: AnaliseImport = { colunas: [], linhas: [] };

    const aplicar = botao(
      "primario",
      {},
      "Aplicar",
    ) as HTMLButtonElement;
    aplicar.disabled = true;

    const analisar = (): void => {
      erroEl.textContent = "";
      analise = analisarImport(cola.value, snap, alvoImplicito);
      desenharPrevia(previa, analise, snap);
      aplicar.disabled = contarAplicaveis(analise) === 0;
    };

    const corpo = el("div", { class: "form-modal" });

    const mostrarResumo = (a: AnaliseImport): void => {
      clear(corpo);
      const aplicados = contarAplicaveis(a);
      const tabelas = new Set(
        a.colunas.filter((c) => c.alvo != null).map((c) => c.alvo),
      ).size;
      corpo.append(
        el(
          "p",
          { class: "aviso aviso-sucesso" },
          `${aplicados} preço(s) aplicado(s) em ${tabelas} tabela(s).`,
        ),
      );

      const colsIgnoradas = a.colunas.filter((c) => c.alvo == null);
      if (colsIgnoradas.length > 0) {
        corpo.append(
          el(
            "p",
            { class: "muted" },
            `Coluna(s) ignorada(s): ${colsIgnoradas
              .map((c) => `"${c.rotulo}" (${c.nota})`)
              .join(", ")}.`,
          ),
        );
      }
      const linhasIgnoradas = a.linhas.filter((l) => !l.tipo);
      if (linhasIgnoradas.length > 0) {
        corpo.append(
          el("p", { class: "muted" }, `${linhasIgnoradas.length} linha(s) ignorada(s):`),
          el(
            "table",
            { class: "mini" },
            el(
              "tbody",
              {},
              ...linhasIgnoradas.map((i) =>
                el(
                  "tr",
                  { class: "import-erro" },
                  el("td", {}, i.rawNome || "(linha em branco)"),
                  el("td", { class: "muted" }, i.nota || "—"),
                ),
              ),
            ),
          ),
        );
      }
      const ok = botao("primario", {}, "Fechar");
      ok.addEventListener("click", fechar);
      corpo.append(el("div", { class: "modal-actions" }, ok));
    };

    arquivo.addEventListener("change", () => {
      const f = arquivo.files?.[0];
      if (!f) return;
      const fr = new FileReader();
      fr.onload = () => {
        cola.value = String(fr.result ?? "");
        analisar();
      };
      fr.onerror = () => {
        erroEl.textContent = "Não foi possível ler o arquivo.";
      };
      fr.readAsText(f, "utf-8");
    });
    cola.addEventListener("input", analisar);

    aplicar.addEventListener("click", () => {
      const desde = isDataISO(dataInp.value) ? dataInp.value : hojeISO();
      if (contarAplicaveis(analise) === 0) return;
      const a = analise;
      aplicar.disabled = true;
      const itens = itensDaAnalise(a);
      void criarAcaoComando(store, erroEl)(
        { agregado: "preco", cmd: { tipo: "aplicarTabela", desde, itens } },
        null, // o resumo abaixo já informa o resultado
        () => {
          aoAplicar();
          mostrarResumo(a);
        },
      ).finally(() => {
        // deu certo: segue desabilitado para não aplicar duas vezes
        if (erroEl.textContent) aplicar.disabled = false;
      });
    });

    const cancelar = botao("secundario", {}, "Cancelar");
    cancelar.addEventListener("click", fechar);

    corpo.append(
      el(
        "p",
        { class: "muted import-dica" },
        "Uma coluna por tabela: “Preço base” e o nome de cada hotel. Colunas de " +
          "hotéis que não existem e peças fora do catálogo são ignoradas. ",
        baixarModelo,
      ),
      el("label", {}, "A Partir de", dataInp),
      el("label", {}, "Arquivo (.csv / .txt)", arquivo),
      el("label", {}, "Ou Cole", cola),
      previa,
      erroEl,
      el("div", { class: "modal-actions" }, cancelar, aplicar),
    );
    return corpo;
  });
}

/** Baixa a matriz de preços em CSV — ver `matrizCsv` em `_precos-parse.ts`. */
export function baixarMatrizCsv(
  snap: Snapshot,
  opts: { tipoIds?: ReadonlySet<string>; hotelIds?: readonly string[] } = {},
): void {
  baixarTexto(nomeArquivo("matriz-precos", null, hojeISO(), "csv"), matrizCsv(snap, opts));
}

function desenharPrevia(
  root: HTMLElement,
  a: AnaliseImport,
  snap: Pick<Snapshot, "clientes">,
): void {
  clear(root);
  if (a.linhas.length === 0) return;

  const nomeAlvo = (alvo: Alvo | null, fallback: string): string =>
    alvo === "base"
      ? "Preço base"
      : alvo == null
        ? fallback
        : (snap.clientes.find((c) => c.id === alvo)?.nome ?? fallback);

  let prontas = 0;
  for (const linha of a.linhas) {
    if (!linha.tipo) continue;
    a.colunas.forEach((c, j) => {
      if (c.alvo != null && linha.celulas[j] != null) prontas++;
    });
  }
  root.append(
    el("p", { class: "muted" }, `${prontas} preço(s) prontos para aplicar.`),
  );

  const cabecalho = el(
    "tr",
    {},
    el("th", {}, "Tipo de peça"),
    ...a.colunas.map((c) =>
      el(
        "th",
        c.alvo == null ? { class: "import-col-off" } : {},
        nomeAlvo(c.alvo, c.rotulo),
        c.alvo == null ? el("small", { class: "muted" }, ` (${c.nota})`) : null,
      ),
    ),
    el("th", {}, ""),
  );
  const corpo = a.linhas.map((linha) =>
    el(
      "tr",
      linha.tipo ? {} : { class: "import-erro" },
      el("td", {}, linha.tipo ? linha.tipo.nome : linha.rawNome),
      ...linha.celulas.map((v, j) =>
        el(
          "td",
          { class: a.colunas[j]!.alvo == null ? "muted r num" : "r num" },
          v == null ? "—" : formatReais(v),
        ),
      ),
      el("td", { class: "muted" }, linha.nota),
    ),
  );
  root.append(
    el(
      "div",
      { class: "table-wrap" },
      el(
        "table",
        { class: "mini" },
        el("thead", {}, cabecalho),
        el("tbody", {}, ...corpo),
      ),
    ),
  );
}
